/**
 * Detects sustained API unavailability (M1-29) and drives the maintenance
 * page — a module-level store, not a hook, for the same reason
 * `lib/authToken.ts` is one: `apiClient`'s middleware is plain code outside
 * React, and it needs one place to REPORT a failure that a component can
 * subscribe to, without threading a callback through every call site.
 * `MaintenanceGate` (`routes/Maintenance.tsx`) is the one subscriber.
 *
 * The state machine, and why each transition exists:
 * - `ok` → `checking`: a request failed in a way a single bad endpoint never
 *   would — `apiClient`'s `onError` (the network never produced a response
 *   at all: connection refused, DNS failure — exactly what a torn-down API
 *   container gives, M0-09) or a gateway status, 502/503/504 (`onResponse`;
 *   see `api/client.ts`'s own note — a torn-down API surfaces to the browser
 *   as a 502 from the PROXY in front of it, not a network-level failure, and
 *   missing that status would have missed the exact scenario this exists
 *   for). A business 500 is deliberately NOT a trigger (the AC's own
 *   "single-endpoint 500 is not maintenance" line) — and neither, really,
 *   needs to be: it is the health check below that actually decides, so a
 *   stray non-platform 503 (a feature genuinely not configured, say) just
 *   costs one invisible extra `/health` call before falling straight back to
 *   `ok`.
 * - `checking` → `ok`: the confirming `/health` call succeeded — the
 *   original failure was isolated, not the platform. Nothing is ever shown
 *   to the user for this path; `checking` and `ok` render identically.
 * - `checking` → `down`: `/health` failed too. NOW it is sustained enough
 *   to say so, and `MaintenanceGate` swaps in the maintenance page.
 * - `down` → `ok`: a later `/health` poll (exponential backoff) succeeds, or
 *   the maintenance flag clears. `MaintenanceGate` reacts by rendering the
 *   app again — no reload.
 *
 * Re-entrancy: `reportApiUnavailable` is a no-op unless the state is
 * currently `ok`. Without that guard, five queries failing in the same
 * render would each kick off their own confirming `/health` call.
 */
import { checkHealth } from './health';
import { fetchMaintenanceFlag } from './maintenanceFlag';

export type MaintenanceStatus = 'ok' | 'checking' | 'down';

export interface MaintenanceState {
  status: MaintenanceStatus;
  /** Set only when `status === 'down'`: which signal put it there. */
  reason?: 'api' | 'flag';
  /** The flag file's own message, when `reason === 'flag'`. */
  message?: string;
}

const INITIAL_BACKOFF_MS = 3_000;
const MAX_BACKOFF_MS = 30_000;
const FLAG_POLL_INTERVAL_MS = 30_000;

const OK: MaintenanceState = { status: 'ok' };

let state: MaintenanceState = OK;
let backoffMs = INITIAL_BACKOFF_MS;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let flagPollTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();
const authRequiredListeners = new Set<() => void>();

function setState(next: MaintenanceState) {
  state = next;
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeMaintenance(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getMaintenanceSnapshot(): MaintenanceState {
  return state;
}

/** Called by `apiClient`'s middleware on a network-level failure or a 503. */
export function reportApiUnavailable(): void {
  if (state.status !== 'ok') {
    return;
  }
  setState({ status: 'checking' });
  void confirmOutage();
}

async function confirmOutage(): Promise<void> {
  const healthy = await checkHealth();
  // Still `checking` unless the flag poll already moved it on — a flag flip
  // mid-confirmation must win either way, not be clobbered back to `ok` OR
  // overwritten with `reason: 'api'`.
  if (state.status !== 'checking') {
    return;
  }
  if (healthy) {
    setState(OK);
    return;
  }
  backoffMs = INITIAL_BACKOFF_MS;
  setState({ status: 'down', reason: 'api' });
  scheduleHealthPoll();
}

function scheduleHealthPoll(): void {
  if (pollTimer) {
    return;
  }
  pollTimer = setTimeout(() => {
    pollTimer = null;
    void (async () => {
      const healthy = await checkHealth();
      // The flag might be what's actually holding this down now; a healthy
      // API does not override a deliberate maintenance window.
      if (healthy && state.reason !== 'flag') {
        backoffMs = INITIAL_BACKOFF_MS;
        setState(OK);
        return;
      }
      if (state.status === 'down' && state.reason === 'api') {
        backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
        scheduleHealthPoll();
      }
    })();
  }, backoffMs);
}

/**
 * Starts polling the maintenance flag (the "deliberate switch", the AC's own
 * words) on a standing interval — safe to run continuously, unlike the
 * health check, since it is one small static file rather than a database
 * round trip. Call once, near the app's root; returns a cleanup function.
 */
export function startMaintenanceFlagPolling(intervalMs = FLAG_POLL_INTERVAL_MS): () => void {
  let cancelled = false;

  async function tick() {
    if (cancelled) {
      return;
    }
    const flag = await fetchMaintenanceFlag();
    if (cancelled) {
      return;
    }
    if (flag.maintenance) {
      setState({ status: 'down', reason: 'flag', message: flag.message });
    } else if (state.status === 'down' && state.reason === 'flag') {
      setState(OK);
    }
  }

  void tick();
  flagPollTimer = setInterval(() => void tick(), intervalMs);
  return () => {
    cancelled = true;
    if (flagPollTimer) {
      clearInterval(flagPollTimer);
      flagPollTimer = null;
    }
  };
}

/** Called by `apiClient`'s middleware on a 401 — never treated as maintenance. */
export function reportAuthRequired(): void {
  for (const listener of authRequiredListeners) {
    listener();
  }
}

export function subscribeAuthRequired(listener: () => void): () => void {
  authRequiredListeners.add(listener);
  return () => authRequiredListeners.delete(listener);
}

/** Test-only: clears module-level state between tests. Not for app code. */
export function __resetMaintenanceForTests(): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  if (flagPollTimer) {
    clearInterval(flagPollTimer);
    flagPollTimer = null;
  }
  backoffMs = INITIAL_BACKOFF_MS;
  state = OK;
  listeners.clear();
  authRequiredListeners.clear();
}
